import axios from 'axios'
import { useCheckoutStore } from '../stores/store'
import { useStore } from '../composables/store'
import braintree from 'braintree-web'
import Swal from 'sweetalert2'
import {
	captureShippingError,
	captureCheckoutError,
	capturePaymentError,
	addCheckoutBreadcrumb,
	CheckoutAction,
	CheckoutFunnelStep,
	ErrorLevel,
	trackAutoRecoveryOutcome,
	trackCheckoutFunnel,
} from './sentry'
import { validateEmail } from './email.js'
import { devLog } from './devLog.js'
import { getOrdersForCart, isCheckoutCompleteOrder } from './orderStatus.js'
import { shippingEstimateFailure } from './networkError.js'

const store = useStore()
const getCheckout = () => useCheckoutStore()

function getCustomerEmail() {
	const checkout = getCheckout()
	return store?.user?.email || checkout.guest?.email || checkout.billing?.billingAddress?.email || ''
}

// Shipping Functions

function formatShippingDescription(description, short) {
	const patterns = [
		{
			match: (desc) => desc.includes('USPS'),
			getName: (desc) => {
				return desc // format for USPS is fine
				if (desc.includes('First-Class Package International Service')) {
					return 'USPS First-Class Package International'
				} else if (desc.includes('Priority Mail International')) {
					return 'USPS Priority Mail International'
				} else if (desc.includes('Priority Mail')) {
					return desc
				} else if (desc.includes('Ground Advantage')) {
					return desc
				}
				return desc
			},
			getType: (desc) => {
				if (desc.includes('First-Class Package International Service')) {
					return 'First-Class Mail'
				} else if (desc.includes('Priority Mail International')) {
					return 'Priority Mail'
				} else if (desc.includes('Priority Mail')) {
					return 'Priority Mail'
				} else if (desc.includes('Ground Advantage')) {
					return 'Ground Advantage'
				}
				return desc
			},
		},
		{
			match: (desc) => desc.includes('UPS'),
			getName: (desc) => {
				if (desc.includes('Next Day Air')) {
					return 'UPS Next Day Air'
				} else if (desc.includes('2nd Day Air')) {
					return 'UPS 2nd Day Air'
				} else if (desc.includes('Worldwide Expedited')) {
					return 'UPS Worldwide Expedited'
				} else if (desc.includes('Worldwide Express')) {
					return 'UPS Worldwide Express'
				} else if (desc.includes('Worldwide Saver')) {
					return 'UPS Worldwide Saver'
				} else if (desc.includes('Ground')) {
					return 'UPS Ground'
				}
				return desc
			},
			getType: (desc) => {
				if (desc.includes('Next Day Air')) {
					return 'Next Day Air'
				} else if (desc.includes('2nd Day Air')) {
					return '2nd Day Air'
				} else if (desc.includes('Expedited')) {
					return 'Expedited'
				} else if (desc.includes('Saver')) {
					return 'Saver'
				} else if (desc.includes('Ground')) {
					return 'Ground'
				}
				return desc
			},
		},
		{
			match: (desc) => desc.includes('Local Pickup'),
			getName: () => 'Local Pickup - Liberty Hill, TX',
			getType: () => 'Pickup',
		},
		{
			match: (desc) => desc.includes('Economy'),
			getName: () => 'Economy',
			getType: () => 'Economy',
		},
	]

	const matchedPattern = patterns.find((pattern) => pattern.match(description))

	if (matchedPattern) {
		const name = matchedPattern.getName(description)
		const type = matchedPattern.getType(description)

		// Extract estimated date
		const estimatedDate = description.includes('Estimated:') ? description.split('Estimated:')[1].trim().replace(')', '') : 'No Date'

		// Include type in the name only if they are different and the name doesn't already contain the type
		const nameWithDescription = name !== type && !name.includes(type) ? `${name} (${type})` : name

		if (short) {
			return name
		}

		//return `${nameWithDescription} | Estimated: ${estimatedDate}`
		return estimatedDate === 'No Date' ? `${nameWithDescription}` : `${nameWithDescription} | Estimated: ${estimatedDate}`
	}

	// Fallback for unknown formats
	return description
}

function cartHasCheckoutItems() {
	return !!(store.cart?.cartItems?.length || store.cart?.certificates?.length)
}

function shippingAddressFieldSummary(address) {
	const required = [
		{ key: 'firstName', label: 'First name' },
		{ key: 'lastName', label: 'Last name' },
		{ key: 'address1', label: 'Street address' },
		{ key: 'city', label: 'City' },
		{ key: 'stateOrProvince', label: 'State' },
		{ key: 'countryCode', label: 'Country' },
		{ key: 'postalCode', label: 'Postal code' },
	]
	const present = []
	const missing = []
	for (const field of required) {
		const value = address?.[field.key]
		if (value != null && String(value).trim() !== '') {
			present.push(field.key)
		} else {
			missing.push(field.label)
		}
	}
	return { present, missing }
}

function buildShippingConsignment(checkout, bcCartId) {
	const addr = checkout.shipping.shippingAddress || {}
	const email = getCustomerEmail()
	return {
		bcCartId,
		address: {
			...(addr.firstName && { first_name: addr.firstName }),
			...(addr.lastName && { last_name: addr.lastName }),
			...(addr.company && { company: addr.company }),
			...(addr.address1 && { address1: addr.address1 }),
			...(addr.address2 && { address2: addr.address2 }),
			...(addr.phone && { phone: addr.phone }),
			...(addr.city && { city: addr.city }),
			...(addr.stateOrProvince && { state_or_province: addr.stateOrProvince }),
			...(addr.countryCode && { country_code: addr.countryCode }),
			...(addr.postalCode && { postal_code: addr.postalCode }),
			// Always stamp the customer email on the consignment so BC can't backfill a
			// placeholder (e.g. a name initial) onto the order's shipping address. Only a
			// valid address is sent — never '' or partial input. (see order-366958 issue)
			...(email && validateEmail(email) && { email }),
		},
	}
}

/** Sync local cart to BC and return true if a bcId is available afterward */
async function syncBcCartAfterNotFound() {
	if (!cartHasCheckoutItems() || typeof store.saveCart !== 'function') return false

	devLog('[checkout] BC cart missing — syncing cart before retrying shipping estimates')
	try {
		await store.saveCart()
	} catch (error) {
		console.error('[checkout] saveCart failed after Not Found:', error)
		return false
	}

	return !!store.cart?.bcId
}

/** Coalesce parallel estimate requests (e.g. post-login fetchAddresses + restoreCheckoutProgress). */
let shippingEstimatesInFlight = null

/**
 * Status-0 has no HTTP body, so the address is not the problem. Stop the spinner
 * and let the shopper retry. Shopper-driven only: the failed POST already waited
 * on the network, and an automatic loop would pin checkout on the spinner.
 */
async function reportShippingEstimateError(error, retry) {
	const checkout = getCheckout()
	const failure = shippingEstimateFailure(error)
	if (!failure.retry) {
		checkout.setShippingQuoteNetworkError(false)
		await Swal.fire({
			icon: 'error',
			title: failure.title,
			text: failure.text,
			confirmButtonText: failure.retryLabel,
		})
		return false
	}

	checkout.setShippingQuoteNetworkError(true)
	const result = await Swal.fire({
		icon: 'warning',
		title: failure.title,
		text: failure.text,
		confirmButtonText: failure.retryLabel,
		showCancelButton: true,
		cancelButtonText: failure.dismissLabel,
		confirmButtonColor: '#e59789',
	})
	if (result.isConfirmed && typeof retry === 'function') {
		return retry()
	}
	return false
}

async function getShippingEstimates(options = {}) {
	if (shippingEstimatesInFlight) {
		return shippingEstimatesInFlight
	}
	shippingEstimatesInFlight = runGetShippingEstimates(options).finally(() => {
		shippingEstimatesInFlight = null
	})
	return shippingEstimatesInFlight
}

async function runGetShippingEstimates({ isRetry = false } = {}) {
	// Creates consignment using selected shipping address
	// Returns shipping options and consignment ID
	// Sets default shipping method on the checkout store and big commerce checkout
	const checkout = getCheckout()
	checkout.setFetchingShippingStatus(true)
	checkout.setShippingQuoteNetworkError(false)

	checkout.setShippingOptions([])
	checkout.selectShipping(null)

	devLog('getShippingEstimates running....', { isRetry })
	if (!store.cart?.bcId) {
		if (!isRetry && cartHasCheckoutItems() && (await syncBcCartAfterNotFound())) {
			return runGetShippingEstimates({ isRetry: true })
		}
		devLog('No cart ID found, returning.')
		checkout.setFetchingShippingStatus(false)
		return false
	}
	const bcCartIdAtRequest = store.cart.bcId
	devLog('BC cart ID: ', bcCartIdAtRequest)

	const addressSummary = shippingAddressFieldSummary(checkout.shipping.shippingAddress)
	if (addressSummary.missing.length) {
		checkout.setFetchingShippingStatus(false)
		addCheckoutBreadcrumb('Shipping estimates skipped — incomplete address', {
			data: addressSummary,
		})
		await Swal.fire({
			icon: 'warning',
			title: 'Incomplete shipping address',
			text: `Please complete: ${addressSummary.missing.join(', ')}`,
			confirmButtonText: 'OK',
		})
		return false
	}

	const consignment = buildShippingConsignment(checkout, bcCartIdAtRequest)
	devLog('consignment: ', consignment)

	try {
		let response = await axios.post(`${store.api}/controllers/bigCommerce/shipping`, consignment)
		if (response?.data?.success) {
			checkout.setShippingOptions(response.data.shippingOptions)
			checkout.updateConsignmentId(response.data.consignmentId)

			devLog('Shipping address: ', checkout.shipping.shippingAddress)

			checkout.setFetchingShippingStatus(false)
			addCheckoutBreadcrumb('Shipping estimates loaded', {
				data: {
					consignmentId: response.data.consignmentId,
					optionsCount: response.data.shippingOptions?.length ?? 0,
				},
			})
			return true
		}

		checkout.setFetchingShippingStatus(false)
		if (response?.data?.errorText === 'Not Found') {
			if (store.cart?.bcId !== bcCartIdAtRequest) {
				devLog('Ignoring stale shipping estimate Not Found (cart id changed during request)')
				return false
			}
			if (!isRetry && (await syncBcCartAfterNotFound())) {
				return runGetShippingEstimates({ isRetry: true })
			}
			devLog('Big Commerce cart not found after sync retry')
			captureShippingError('BigCommerce cart not found when fetching shipping estimates', {
				action: CheckoutAction.GET_SHIPPING_ESTIMATES,
				store,
				checkout,
				consignment,
				extra: { errorText: response?.data?.errorText, response: response?.data, isRetry },
			})
			return Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Oh no! 🫤 Your cart no longer exists in the database. Please remove all items and re-populate the cart to continue with checkout.',
				confirmButtonText: 'OK',
			})
		}

		captureShippingError(response?.data?.errorText || 'Shipping estimates request failed', {
			action: CheckoutAction.GET_SHIPPING_ESTIMATES,
			store,
			checkout,
			consignment,
			extra: {
				response: response?.data,
				isRetry,
				httpStatus: response?.data?.status ?? response?.status,
				addressFields: addressSummary,
			},
		})
		return Swal.fire({
			icon: 'error',
			title: 'Error',
			text: 'There was an error getting shipping estimates. Please verify your address info is accurate and try again.',
			confirmButtonText: 'OK',
		})
	} catch (error) {
		checkout.setFetchingShippingStatus(false)
		console.error('Error fetching shipping estimates:', error)
		captureShippingError(error, {
			action: CheckoutAction.GET_SHIPPING_ESTIMATES,
			store,
			checkout,
			consignment,
		})
		return reportShippingEstimateError(error, () => runGetShippingEstimates())
	}
}

async function getShippingEstimatesNew(selectedAddress, { isRetry = false } = {}) {
	devLog('getShippingEstimatesNew running....', { isRetry })
	if (!store.cart?.bcId) {
		if (!isRetry && cartHasCheckoutItems() && (await syncBcCartAfterNotFound())) {
			return getShippingEstimatesNew(selectedAddress, { isRetry: true })
		}
		devLog('No cart ID found, returning.')
		return { success: false, error: 'No cart ID found' }
	}
	const bcCartIdAtRequest = store.cart.bcId
	devLog('BC cart ID: ', bcCartIdAtRequest)

	const customerEmail = getCustomerEmail()
	let consignment = {
		bcCartId: bcCartIdAtRequest,
		address: {
			...(selectedAddress?.firstName && { first_name: selectedAddress.firstName }),
			...(selectedAddress?.lastName && { last_name: selectedAddress.lastName }),
			...(selectedAddress?.company && { company: selectedAddress.company }),
			...(selectedAddress?.address1 && { address1: selectedAddress.address1 }),
			...(selectedAddress?.address2 && { address2: selectedAddress.address2 }),
			...(selectedAddress?.phone && { phone: selectedAddress.phone }),
			...(selectedAddress?.city && { city: selectedAddress.city }),
			...(selectedAddress?.stateOrProvince && { state_or_province: selectedAddress.stateOrProvince }),
			...(selectedAddress?.countryCode && { country_code: selectedAddress.countryCode }),
			...(selectedAddress?.postalCode && { postal_code: selectedAddress.postalCode }),
			// Stamp a valid customer email so BC can't backfill a placeholder (see order-366958).
			...(customerEmail && validateEmail(customerEmail) && { email: customerEmail }),
		},
	}

	devLog('consignment: ', consignment)

	try {
		let response = await axios.post(`${store.api}/controllers/bigCommerce/shipping`, consignment)
		if (response?.data?.success) {
			getCheckout().setShippingQuoteNetworkError(false)
			return { success: true, data: response.data.shippingOptions, consignmentId: response.data.consignmentId }
		} else {
			if (response?.data?.errorText === 'Not Found') {
				if (store.cart?.bcId !== bcCartIdAtRequest) {
					devLog('Ignoring stale shipping estimate Not Found (cart id changed during request)')
					return { success: false, error: 'stale_cart_id', stale: true }
				}
				if (!isRetry && (await syncBcCartAfterNotFound())) {
					return getShippingEstimatesNew(selectedAddress, { isRetry: true })
				}
				devLog('Big Commerce cart not found after sync retry')
				captureShippingError('BigCommerce cart not found when fetching shipping estimates', {
					action: CheckoutAction.GET_SHIPPING_ESTIMATES,
					store,
					consignment,
					extra: { errorText: response?.data?.errorText, response: response?.data, isRetry },
				})
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Oh no! 🫤 Your cart no longer exists in the database. Please remove all items and re-populate the cart to continue with checkout.',
					confirmButtonText: 'OK',
				})
			} else {
				captureShippingError(response?.data?.errorText || 'Shipping estimates request failed', {
					action: CheckoutAction.GET_SHIPPING_ESTIMATES,
					store,
					consignment,
					extra: { response: response?.data, selectedAddress, isRetry },
				})
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'There was an error getting shipping estimates. Please verify your address info is accurate and try again.',
					confirmButtonText: 'OK',
				})
			}
			return { success: false, error: response?.data?.errorText || 'Unknown error' }
		}
	} catch (error) {
		console.error('Error fetching shipping estimates:', error)
		captureShippingError(error, {
			action: CheckoutAction.GET_SHIPPING_ESTIMATES,
			store,
			consignment,
			extra: { selectedAddress },
		})
		const retried = await reportShippingEstimateError(error, () => getShippingEstimatesNew(selectedAddress))
		if (retried && retried.success) return retried
		return { success: false, error: error?.message || 'Network error' }
	}
}

async function updateShippingAddress(data = null) {
	const checkout = getCheckout()
	var consignment
	if (!data) {
		consignment = {
			address: checkout.shipping.shippingAddress,
			bcCartId: store?.cart?.bcId,
			consignmentId: checkout.shipping.consignmentId,
		}
	} else {
		consignment = { ...data }
	}
	devLog('updateShippingAddress running data: ', data)
	const url = `${store.api}/controllers/bigCommerce/shipping`

	try {
		const response = await axios.put(url, data)
		devLog('updateShippingAddress response: ', response?.data)
		return response?.data
	} catch (error) {
		console.error('Error updating shipping address:', error)
		captureShippingError(error, {
			action: CheckoutAction.UPDATE_SHIPPING_ADDRESS,
			store,
			checkout,
			extra: { requestData: data },
		})
		return { success: false, error: error?.message || 'Failed to update shipping address' }
	}
}

/** Flatten BC API error payloads for Sentry breadcrumbs (axios errors are not JSON-safe). */
function breadcrumbBcShippingErrorPayload(apiBody) {
	if (!apiBody) return {}

	const { message, errorData, data: bcResponseBody } = apiBody
	if (!errorData) {
		return { message: message ?? null, bcResponse: bcResponseBody ?? null }
	}

	const { checkoutId, consignmentId, selectedShipping, url, bcError } = errorData
	const bcErrorPayload =
		bcError?.response?.data ??
		bcError?.data ??
		(bcError?.message
			? { message: bcError.message, status: bcError.response?.status, statusText: bcError.response?.statusText }
			: bcError)

	return {
		message: message ?? null,
		checkoutId,
		consignmentId,
		selectedShipping,
		url,
		bcError: bcErrorPayload ?? null,
	}
}

async function selectShippingMethod({ allowRecovery = true } = {}) {
	const checkout = getCheckout()
	let isDigitalOnly = !store?.cart?.cartItems?.length && !!store?.cart?.certificates?.length
	const data = {
		checkoutId: store?.cart?.bcId,
		consignmentId: checkout.shipping.consignmentId,
		selectedShippingId: checkout.shipping?.selectedShipping?.id,
		isDigitalOnly,
	}

	if (!isDigitalOnly && (!data.checkoutId || !data.consignmentId || !data.selectedShippingId)) {
		devLog('selectShippingMethod skipped — missing checkout session fields', data)
		return { success: false, error: 'Missing checkout shipping session' }
	}

	try {
		const response = await axios.post(`${store.api}/controllers/bigCommerce/checkout/shipping`, data)
		devLog('update consignment with selected shipping: ', response?.data)

		if (response?.data?.data) {
			updateCheckoutTotals(response?.data?.data)
			addCheckoutBreadcrumb('Shipping method selected', {
				data: { shippingId: data.selectedShippingId, isDigitalOnly },
			})
			return { success: true }
		}

		if (response?.data?.success === false) {
			addCheckoutBreadcrumb('selectShippingMethod: BC error', {
				category: 'checkout.shipping',
				level: 'warning',
				data: {
					...breadcrumbBcShippingErrorPayload(response.data),
					allowRecovery,
					request: data,
				},
			})
		}

		// BC rejected the selection (often stale consignment after guest login + parallel quote fetches)
		if (allowRecovery && response?.data?.success === false && checkout.shipping.shippingAddress?.address1) {
			const methodId = data.selectedShippingId
			devLog('selectShippingMethod failed — refreshing estimates and retrying once', response?.data)
			const estimatesOk = await getShippingEstimates()
			if (estimatesOk === true && methodId) {
				const match = checkout.shipping.shippingOptions?.find((option) => option.id === methodId)
				if (match) {
					checkout.selectShipping(match)
					return selectShippingMethod({ allowRecovery: false })
				}
			}
		}

		console.error('selectShippingMethod: No data in response', response?.data)
		addCheckoutBreadcrumb('selectShippingMethod: empty response (post-recovery)', {
			category: 'checkout.shipping',
			level: 'warning',
			data: { response: response?.data, request: data },
		})
		return { success: false, error: response?.data?.message || 'No data in response' }
	} catch (error) {
		console.error('Error selecting shipping method:', error)
		addCheckoutBreadcrumb('selectShippingMethod: BC error', {
			category: 'checkout.shipping',
			level: 'warning',
			data: {
				...breadcrumbBcShippingErrorPayload(error?.response?.data),
				httpStatus: error?.response?.status ?? null,
				request: data,
			},
		})
		captureShippingError(error, {
			action: CheckoutAction.SELECT_SHIPPING_METHOD,
			store,
			checkout,
			extra: { isDigitalOnly },
		})
		return { success: false, error: error?.message || 'Failed to select shipping method' }
	}
}

// Braintree Functions

async function initializeVenmo(data) {
	const { clientToken, amount } = data
	let venmoInstance

	try {
		devLog('Initializing braintree client instance...')
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		devLog('Initializing braintree venmo instance...')
		venmoInstance = await braintree.venmo.create({
			client: clientInstance,
			allowDesktopWebLogin: true,
			allowNewBrowserTab: false,
			mobileWebFallBack: true,
			// allowDesktop: true,
			paymentMethodUsage: 'single_use',
			totalAmount: amount,
		})

		devLog('venmoInstance: ', venmoInstance)
		devLog('venmoInstance string: ', JSON.stringify(venmoInstance))

		if (!venmoInstance.isBrowserSupported()) {
			devLog('Venmo is not supported in this browser.')
			return { success: false, message: 'Venmo is not supported in this browser.' }
		}

		devLog('Tokenizing Venmo payment...')
		const payload = await venmoInstance.tokenize()
		devLog('Venmo payment tokenized. Nonce:', payload.nonce)
		return { success: true, nonce: payload.nonce, clientInstance }
	} catch (error) {
		console.error('Error initializing Venmo checkout:', error)
		if (error?.code !== 'VENMO_DESKTOP_CANCELED' && error?.code !== 'VENMO_CANCELED') {
			capturePaymentError(error, {
				paymentMethod: 'venmo',
				action: CheckoutAction.TOKENIZE_PAYMENT,
				store,
				extra: { phase: 'initializeVenmo' },
			})
		}
		return { success: false, error }
	} finally {
		if (venmoInstance) {
			venmoInstance.teardown()
		}
	}
}

async function processBraintreePayment(data) {
	const { nonce, amount, bcOrderId, deviceData, bcCartId } = data
	try {
		const paymentData = {
			paymentMethodNonce: nonce,
			amount: amount,
			bcOrderId,
			bcCartId,
		}
		if (deviceData) paymentData.deviceData = deviceData

		const response = await axios.post(`${store.api}/controllers/braintree/processPayment`, paymentData)

		if (response.data.success) {
			devLog('Payment successful! Braintree Transaction ID:', response.data.data.id)
			return response.data
		} else {
			console.error('Payment failed:', response.data.error)
			capturePaymentError('Process Braintree Payment Failed', {
				paymentMethod: 'braintree',
				processor: 'braintree',
				action: CheckoutAction.PROCESS_BRAINTREE_PAYMENT,
				bcOrderId,
				store,
				checkout: getCheckout(),
				extra: { response: response?.data },
			})
		}
	} catch (error) {
		console.error('Error processing payment:', error)
		capturePaymentError(error, {
			paymentMethod: 'braintree',
			processor: 'braintree',
			action: CheckoutAction.PROCESS_BRAINTREE_PAYMENT,
			bcOrderId,
			store,
			checkout: getCheckout(),
		})
		throw error
	}
}

async function getClientToken() {
	// takes this.store.api from the frontend as an argument
	devLog('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${store.api}/controllers/braintree/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting client token:', error)
		captureCheckoutError(error, {
			component: 'checkout',
			action: CheckoutAction.GET_CLIENT_TOKEN,
			store,
		})
		return { success: false, message: 'Error getting client token' }
	}
}

async function getSandboxClientToken() {
	// takes this.store.api from the frontend as an argument
	devLog('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${store.api}/controllers/braintree/sandbox/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting sandbox client token:', error)
		captureCheckoutError(error, {
			component: 'checkout',
			action: 'getSandboxClientToken',
			store,
		})
		return { success: false, message: 'Error getting client token' }
	}
}

async function fetchBCCheckout() {
	try {
		const response = await axios.get(`${store.api}/controllers/bigCommerce/checkout/${store.cart?.bcId}`)
		const checkoutSession = response?.data?.checkout

		return checkoutSession
	} catch (error) {
		console.error('Error fetching checkout session:', error)
		captureCheckoutError(error, {
			component: 'checkout',
			action: CheckoutAction.FETCH_BC_CHECKOUT,
			store,
		})
		return null
	}
}

async function validateConsignment() {
	const checkout = getCheckout()
	if (checkout.isDigitalOnly) return true
	// for validating the consignment right before placeOrder
	const checkoutSession = await fetchBCCheckout()
	const consignment = checkoutSession?.consignments?.[0]
	const selectedShipping = consignment?.selected_shipping_option
	return !!(
		checkoutSession?.id &&
		consignment?.id &&
		selectedShipping?.id &&
		consignment?.id === checkout.shipping.consignmentId &&
		selectedShipping?.id === checkout.shipping.selectedShipping?.id
	)
}

/**
 * Re-post shipping address quotes and re-select the method when Pinia and BC checkout drift.
 * @returns {Promise<boolean>}
 */
async function recoverMissingConsignment() {
	const checkout = getCheckout()
	if (checkout.isDigitalOnly) return true

	if (await validateConsignment()) return true
	if (!checkout.shipping.shippingAddress?.address1) {
		trackAutoRecoveryOutcome('skipped', { syncBug: 'CONSIGNMENT_DRIFT', reason: 'no-shipping-address' })
		return false
	}

	try {
		const estimatesOk = await getShippingEstimates()
		if (estimatesOk !== true) {
			trackAutoRecoveryOutcome('failed', { syncBug: 'CONSIGNMENT_DRIFT', reason: 'shipping-estimates-failed' })
			return false
		}
		if (checkout.shipping.selectedShipping?.id) {
			await selectShippingMethod()
		}
		const recovered = await validateConsignment()
		trackAutoRecoveryOutcome(recovered ? 'success' : 'failed', { syncBug: 'CONSIGNMENT_DRIFT' })
		return recovered
	} catch (error) {
		trackAutoRecoveryOutcome('failed', { syncBug: 'CONSIGNMENT_DRIFT', reason: 'exception' })
		captureCheckoutError(error, {
			component: 'checkout',
			action: CheckoutAction.VALIDATE_CONSIGNMENT,
			store,
			checkout,
			level: ErrorLevel.WARNING,
			extra: { phase: 'recoverMissingConsignment' },
		})
		return false
	}
}

/**
 * Ensure a BC checkout session exists for the current cart; recreate cart once if fetch fails.
 * @returns {Promise<{ success: boolean, checkoutSession?: object, recreated?: boolean, reason?: string }>}
 */
async function ensureBcCheckoutSession() {
	if (!store?.cart?.bcId) {
		if (typeof store.saveCart === 'function') {
			await store.saveCart()
		}
	}
	if (!store?.cart?.bcId) {
		return { success: false, reason: 'no-cart' }
	}

	let checkoutSession = await fetchCheckoutTotals()
	if (checkoutSession?.id) {
		return { success: true, checkoutSession }
	}

	if (typeof store.saveCart === 'function') {
		await store.saveCart()
	}
	if (!store?.cart?.bcId) {
		return { success: false, reason: 'recreate-failed' }
	}

	checkoutSession = await fetchCheckoutTotals()
	if (checkoutSession?.id) {
		return { success: true, checkoutSession, recreated: true }
	}

	return { success: false, reason: 'fetch-failed' }
}

/**
 * User-facing payment error copy when a BC order id exists for retry.
 */
function formatPaymentFailureMessage(message, bcOrderId) {
	const base = message || 'There was an error completing your order. Please try again or contact customer support.'
	if (!bcOrderId) return base
	return `${base} Your order was saved (reference #${bcOrderId}). You can retry payment without placing a new order.`
}

function persistBcOrderIdFromResponse(checkout, data) {
	const id = data?.bcOrderId
	if (id) checkout.setBcOrderId(id)
}

// Checkout Functions
export async function fetchCheckoutTotals() {
	const checkoutSession = await fetchBCCheckout()
	updateCheckoutTotals(checkoutSession)
	return checkoutSession
}

function updateCheckoutTotals(checkoutSession) {
	const checkout = getCheckout()
	if (!checkoutSession?.id) {
		devLog('checkout session missing id')
		return false
	}
	const taxes = checkoutSession?.taxes

	checkout.updateTaxes(taxes)

	let baseAmount = checkoutSession?.cart?.base_amount
	let cartAmountExTax = checkoutSession?.cart?.cart_amount_ex_tax
	let cartAmountIncTax = checkoutSession?.cart?.cart_amount_inc_tax
	let subTotalExTax = checkoutSession?.subtotal_ex_tax
	let subTotalIncTax = checkoutSession?.subtotal_inc_tax
	let taxTotal = checkoutSession?.tax_total
	let grandTotal = checkoutSession?.grand_total

	store.cart.subTotal = baseAmount
	store.cart.tax = taxTotal
	store.cart.total = grandTotal - checkoutSession?.shipping_cost_total_ex_tax

	store.cart.baseAmount = baseAmount
	store.cart.discountAmount = checkoutSession?.cart?.discount_amount
	store.cart.taxIncluded = checkoutSession?.cart?.tax_included
	store.cart.manualDiscountAmount = checkoutSession?.cart?.manual_discount_amount
	store.cart.cartAmountIncTax = checkoutSession?.cart?.cart_amount_inc_tax
	store.cart.cartAmountExTax = checkoutSession?.cart?.cart_amount_ex_tax

	return true
}

function parseExpiryDate(expiryDate) {
	const [month, year] = expiryDate.split(' / ').map(Number)
	const expiryYear = year < 100 ? 2000 + year : year // Assuming year is in the format "xx"
	return {
		expiryMonth: month,
		expiryYear: expiryYear,
	}
}

function validateCheckoutInfo(paymentMethod = '') {
	const checkout = getCheckout()
	// Validate email (either logged in user or guest)
	const customerEmail = getCustomerEmail()
	if (!customerEmail || !validateEmail(customerEmail)) {
		return { success: false, message: 'Please provide a valid email address to continue.' }
	}

	// Validate billing address
	if (!checkout.billing?.billingAddress?.postalCode) {
		return { success: false, message: 'Please provide a billing address to continue.' }
	}

	// Validate shipping address for physical products
	if (!checkout.shipping?.shippingAddress?.address1 && !!store?.cart?.cartItems?.length) {
		return { success: false, message: 'Please provide a shipping address to continue.' }
	}

	// Skip payment validation if not required
	if (checkout.paymentNotRequired) {
		return { success: true, message: 'Checkout information is valid' }
	}

	// Validate payment method (credit card)
	if (paymentMethod === 'cards') {
		if (checkout.payment.addingNewCard) {
			const newCard = checkout.payment.newCard
			const errors = []

			if (!newCard.number) errors.push('card number')
			if (!newCard.expiry) errors.push('expiration date')
			if (!newCard.name) errors.push('cardholder name')
			if (!newCard.cvv) errors.push('CVV')

			if (errors.length > 0) {
				return {
					success: false,
					message: `Please provide the following card information: ${errors.join(', ')}`,
				}
			}
		} else {
			const storedCard = checkout.payment.selectedCard
			if (storedCard?.type === 'stored_card') {
				if (!storedCard.last4) {
					return { success: false, message: 'Invalid stored card: Missing card number' }
				}
				if (!storedCard.token) {
					return { success: false, message: 'Invalid stored card: Missing card token' }
				}
				if (!checkout.payment.cardCVV) {
					return { success: false, message: 'Please enter the CVV for your stored card' }
				}
			} else {
				return { success: false, message: 'Please select a valid payment method' }
			}
		}
	}

	// Validate payment method (ACH)
	if (paymentMethod === 'ach') {
		if (checkout.payment.addingNewACH) {
			const newACH = checkout.payment.newACH
			const errors = []

			// Required fields for all ACH types
			if (!newACH.accountType) errors.push('account type')
			if (!newACH.accountNumber) errors.push('account number')
			if (!newACH.routingNumber) errors.push('routing number')
			if (!newACH.ownershipType) errors.push('ownership type')

			// Conditional validation based on ownershipType
			if (newACH.ownershipType === 'personal') {
				if (!newACH.firstName) errors.push('first name')
				if (!newACH.lastName) errors.push('last name')
			} else {
				if (!newACH.businessName) errors.push('business name')
			}

			if (errors.length > 0) {
				return {
					success: false,
					message: `Please provide the following ACH information: ${errors.join(', ')}`,
				}
			}
		} else {
			// Validate selected stored ACH
			const selectedACH = checkout.payment.selectedACH
			if (!selectedACH?.token) {
				return { success: false, message: 'Please select a valid ACH account' }
			}
			if (!selectedACH.firstName || !selectedACH.lastName) {
				return { success: false, message: 'Invalid ACH account: Missing account holder name' }
			}
		}
	}

	return { success: true, message: 'Checkout information is valid' }
}

/**
 * Block express wallet sheets until the same fields as expressCheckoutValidated are complete.
 * @returns {Promise<boolean>}
 */
async function requireExpressCheckoutValidated() {
	const checkout = getCheckout()
	const validation = checkout.expressCheckoutValidated
	if (validation.value) return true

	await Swal.fire({
		icon: 'warning',
		title: 'Missing Required Fields',
		text: validation.message,
		confirmButtonText: 'OK',
		confirmButtonColor: '#e59789',
	})
	return false
}

/**
 * Shared post-payment success path: reset checkout state, clear cart, guest order, Algolia.
 * @param {{ data?: { order?: object, redirectUrl?: string } }} processPayment
 * @returns {Promise<string|undefined>} redirectUrl for order confirmation
 */
async function completeCheckoutSuccess(processPayment) {
	const checkout = getCheckout()
	checkout.resetShippingAddress()
	checkout.checkoutSuccess = true
	await clearCart()

	if (!store?.user?.id && processPayment?.data?.order) {
		try {
			window.localStorage.setItem('guestOrder', JSON.stringify(processPayment.data.order))
		} catch (storageError) {
			console.error('Error saving guest order to localStorage:', storageError)
		}
	}

	try {
		await checkout.triggerAlgoliaPurchaseEvent()
	} catch (e) {
		console.error('Non-critical: Algolia purchase event failed', e)
	}

	trackCheckoutFunnel(CheckoutFunnelStep.PAYMENT_SUCCESS, {
		paymentMethod: processPayment?.data?.paymentMethod || null,
		bcOrderId: processPayment?.data?.bcOrderId || checkout.bcOrderId || null,
	})

	return processPayment?.data?.redirectUrl
}

// ── Resilient place-order (immediate kickoff + status polling) ──────────────────
//
// "Place Order" used to be one multi-second request; on flaky mobile networks it
// would time out client-side and tell the shopper the order failed even though the
// backend had completed it. Now the backend kicks the order off and returns
// immediately, and we poll a lightweight status endpoint until the order reaches a
// terminal state. A dropped request is no longer fatal — the next poll sees the
// real outcome. Capped at 25s; if still processing after that we treat it as failed
// (the backend is idempotent, so retrying is safe and won't double-charge).
//
// Cap sized from production data: real card checkouts usually complete in ~5–10s, but large
// carts and slow gateway days need more headroom. Recovery below also checks BC orders if
// the poll cap is hit while the backend is still finishing.
const PLACE_ORDER_POLL_CAP_MS = 45000
const PLACE_ORDER_POLL_INTERVAL_MS = 2000
const PLACE_ORDER_KICKOFF_TIMEOUT_MS = 6000
const PLACE_ORDER_STATUS_TIMEOUT_MS = 5000

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Map a kickoff/status response body to a terminal, axios-like `{ data, status }`
 * result the existing payment methods already know how to handle — or null if the
 * order is still processing / not yet started.
 */
function terminalCheckoutResult(body) {
	if (!body) return null
	if (body.processingState === 'completed' && body.success) {
		return { data: { ...body, success: true }, status: 200 }
	}
	if (body.processingState === 'failed') {
		return { data: { ...body, success: false }, status: body.status || 500 }
	}
	return null
}

/** After poll cap: one last status read, then BC order lookup (fixes BW / slow checkouts). */
async function recoverCheckoutAfterPollTimeout({ api, checkoutId }) {
	const statusUrl = `${api}/controllers/checkout/status`

	try {
		const res = await axios.get(statusUrl, { params: { checkoutId }, timeout: PLACE_ORDER_STATUS_TIMEOUT_MS })
		const terminal = terminalCheckoutResult(res?.data)
		if (terminal) {
			addCheckoutBreadcrumb('Checkout recovered on post-timeout status poll', {
				category: 'checkout.poll',
				data: { checkoutId, processingState: res?.data?.processingState },
			})
			return terminal
		}
	} catch {
		// non-fatal — fall through to order lookup
	}

	try {
		const cart = store?.cart?.bcId === checkoutId ? store.cart : { bcId: checkoutId }
		const orders = await getOrdersForCart(cart)
		const completed = orders.find(isCheckoutCompleteOrder)
		if (completed?.bcId != null) {
			addCheckoutBreadcrumb('Checkout recovered via BC order lookup after poll timeout', {
				category: 'checkout.poll',
				data: { checkoutId, bcOrderId: completed.bcId },
			})
			trackAutoRecoveryOutcome('success', { checkoutId, bcOrderId: completed.bcId, recoveryType: 'checkout_poll_timeout' })
			return {
				data: {
					success: true,
					processingState: 'completed',
					bcOrderId: completed.bcId,
					redirectUrl: `/checkout/order-confirmation/${completed.bcId}`,
				},
				status: 200,
			}
		}
	} catch (error) {
		addCheckoutBreadcrumb('Checkout post-timeout order lookup failed', {
			category: 'checkout.poll',
			level: 'warning',
			data: { checkoutId, message: error?.message },
		})
	}

	trackAutoRecoveryOutcome('failure', { checkoutId, recoveryType: 'checkout_poll_timeout' })
	return null
}

async function kickoffOrder(url, payload) {
	try {
		return await axios.post(url, payload, { timeout: PLACE_ORDER_KICKOFF_TIMEOUT_MS })
	} catch (error) {
		addCheckoutBreadcrumb('Order kickoff request failed — will poll for status', {
			category: 'checkout.poll',
			level: 'warning',
			data: { message: error?.message, httpStatus: error?.response?.status },
		})
		// A terminal body on a non-2xx (e.g. validation) is still useful; otherwise poll.
		return error?.response || null
	}
}

/**
 * Place an order and resolve only once the backend reaches a terminal state (or the
 * ~25s cap). Returns an axios-like `{ data, status }` so callers handle it exactly
 * like the previous single POST response.
 *
 * @param {object} args
 * @param {string} args.api         store.api base url
 * @param {string} args.endpoint    'cards' | 'ach' | 'net30' | 'credit'
 * @param {object} args.payload     the checkout body (same as the old POST body)
 * @param {number} args.checkoutId  store.cart.bcId — used for status polling
 */
async function placeOrderWithPolling({ api, endpoint, payload, checkoutId }) {
	const kickoffUrl = `${api}/controllers/checkout/${endpoint}`
	const statusUrl = `${api}/controllers/checkout/status`
	const deadline = Date.now() + PLACE_ORDER_POLL_CAP_MS

	const checkout = getCheckout()
	checkout.updateOverlayData({
		message: 'Processing your order… please keep this window open.',
		spinnerColor: 'border-t-white',
		spinnerSize: 'h-8 w-8',
	})
	addCheckoutBreadcrumb('Place order started', { category: 'checkout.poll', data: { endpoint, checkoutId } })

	// 1) Kick the order off. If the backend already finished it (idempotent retry) we
	//    get a terminal result right here; otherwise it returns `processing`.
	const kickoff = await kickoffOrder(kickoffUrl, payload)
	const kickoffTerminal = terminalCheckoutResult(kickoff?.data)
	if (kickoffTerminal) return kickoffTerminal

	// 2) Poll until terminal or the cap. Transient poll failures are ignored.
	while (Date.now() < deadline) {
		await sleep(PLACE_ORDER_POLL_INTERVAL_MS)

		let statusBody = null
		try {
			const res = await axios.get(statusUrl, { params: { checkoutId }, timeout: PLACE_ORDER_STATUS_TIMEOUT_MS })
			statusBody = res?.data
		} catch (error) {
			addCheckoutBreadcrumb('Order status poll failed (transient)', { category: 'checkout.poll', level: 'info', data: { message: error?.message } })
			continue
		}

		const terminal = terminalCheckoutResult(statusBody)
		if (terminal) return terminal

		// No job running server-side — the kickoff likely never arrived. Re-issue it
		// (the backend is idempotent, so this can't create a duplicate order/charge).
		if (statusBody?.processingState === 'idle' && Date.now() < deadline) {
			const retry = await kickoffOrder(kickoffUrl, payload)
			const retryTerminal = terminalCheckoutResult(retry?.data)
			if (retryTerminal) return retryTerminal
		}
	}

	// 3) Timed out — try recovery before telling the shopper it failed.
	addCheckoutBreadcrumb('Place order timed out after poll cap', { category: 'checkout.poll', level: 'warning', data: { endpoint, checkoutId, capMs: PLACE_ORDER_POLL_CAP_MS } })
	const recovered = await recoverCheckoutAfterPollTimeout({ api, checkoutId })
	if (recovered) return recovered

	return {
		data: {
			success: false,
			timedOut: true,
			code: 'CHECKOUT_TIMEOUT',
			message:
				"We couldn't confirm your order in time. It may still have gone through — please check your email and your account orders before trying again. Retrying won't charge you twice. If you're unsure, contact customer support.",
		},
		status: 504,
	}
}

async function clearCart() {
	// ## Carts are no longer deleted ##
	// ## Carts are now completed ({completed: true, completedDate: new Date(), bcOrderId: newOrder}) after ##
	// if (store?.user?.id) {
	// 	devLog(`Clearing converted cart ${store.cart.id}...`)
	// 	try {
	// 		const deleteCart = await axios.post(`${store.api}/controllers/bigCommerce/checkout/carts/delete`, { id: store.cart.id })
	// 		devLog(deleteCart?.data)
	// 	} catch (error) {
	// 		console.error('Error clearing converted cart', error)
	// 	}
	// }

	devLog('Resetting store cart...')
	store.cart = { cartItems: [], certificates: [] }
	store.originalCart = { cartItems: [], certificates: [] }
	try {
		window.sessionStorage.removeItem('checkoutInitBcId')
	} catch {
		// ignore storage errors
	}
	devLog('store cart reset')
}

/**
 * After create_order stock failures, remove/clamp affected lines and rebuild the BC cart.
 * @returns {Promise<boolean>} true when stock recovery was applied
 */
async function handlePlaceOrderStockFailure(failureData) {
	const data = failureData || {}
	const oosItems = Array.isArray(data.oosItems) ? data.oosItems : []
	const lowStockItems = Array.isArray(data.lowStockItems) ? data.lowStockItems : []
	const productBcIds = Array.isArray(data.productBcIds) ? data.productBcIds : []

	const isStockFailure =
		data.code === 'CREATE_ORDER_STOCK_FAILED' || oosItems.length > 0 || lowStockItems.length > 0 || productBcIds.length > 0

	if (!isStockFailure || !store?.cart?.cartItems?.length) return false

	const lowStockByProduct = new Map()
	for (const item of lowStockItems) {
		if (item?.productBcId == null) continue
		lowStockByProduct.set(item.productBcId, item.inventoryLevel ?? 0)
	}

	const removedNames = []
	const clampedNames = []
	const oosIdSet = new Set(oosItems.map((item) => item.productBcId).filter(Boolean))

	store.cart.cartItems = (store.cart.cartItems || [])
		.map((ci) => {
			const bcId = ci.item?.bcId || ci.product?.bcId
			const name = ci.item?.name || ci.product?.name || 'Item'

			if (oosIdSet.has(bcId) || (productBcIds.includes(bcId) && !lowStockByProduct.has(bcId))) {
				removedNames.push(name)
				return null
			}

			if (lowStockByProduct.has(bcId)) {
				const nextQty = lowStockByProduct.get(bcId)
				if (nextQty <= 0) {
					removedNames.push(name)
					return null
				}
				if ((ci.quantity || 0) > nextQty) {
					clampedNames.push(`${name} (now ${nextQty})`)
					return { ...ci, quantity: nextQty }
				}
			}

			return ci
		})
		.filter(Boolean)

	// Deduplicate display names
	const uniqueRemoved = [...new Set(removedNames)]
	const uniqueClamped = [...new Set(clampedNames)]

	if (!uniqueRemoved.length && !uniqueClamped.length) return false

	if (typeof store.saveCart === 'function') {
		await store.saveCart()
	} else if (store.originalCart) {
		store.originalCart = JSON.parse(JSON.stringify(store.cart))
	}

	const parts = []
	if (uniqueRemoved.length) {
		parts.push(`Removed from your cart: <b>${uniqueRemoved.join(', ')}</b>`)
	}
	if (uniqueClamped.length) {
		parts.push(`Quantities updated: <b>${uniqueClamped.join(', ')}</b>`)
	}
	parts.push(data.message ? `<p class="mt-2 text-sm">${data.message}</p>` : '')

	await Swal.fire({
		icon: 'warning',
		title: 'Insufficient Stock Available',
		html: parts.filter(Boolean).join('<br/>'),
		confirmButtonText: 'OK',
		confirmButtonColor: '#e59789',
	})

	return true
}

export default {
	formatShippingDescription,
	getShippingEstimates,
	getShippingEstimatesNew,
	processBraintreePayment,
	getClientToken,
	getSandboxClientToken,
	completeCheckoutSuccess,
	placeOrderWithPolling,
	requireExpressCheckoutValidated,
	clearCart,
	handlePlaceOrderStockFailure,
	updateShippingAddress,
	initializeVenmo,
	fetchCheckoutTotals,
	updateCheckoutTotals,
	selectShippingMethod,
	validateCheckoutInfo,
	validateConsignment,
	recoverMissingConsignment,
	ensureBcCheckoutSession,
	formatPaymentFailureMessage,
	persistBcOrderIdFromResponse,
	getCustomerEmail,
}
